import { useState, useContext, useEffect, FC } from "react"
import Calendar from "../../components/scheduling/calendar/Calendar"
import CalendarDetails from "../../components/account-settings/calendars/CalendarDetails"
import CalendarLoadingSpinner from "../../components/scheduling/calendar/CalendarLoadingSpinner"
import NewMeeting from "../../components/scheduling/new-meeting/NewMeeting"
import useLoading from "../../hooks/useLoading"
import { Button } from "../../components/ui/button"
import { CalendarRequests } from "../../api/app.service"
import { ConnectCalendarCTA } from "../../components/account-settings/calendars/ConnectCalendarCTA"
import { defaultAvailability } from "../../objects/availabilityObjects"
import { Link, useLocation } from "react-router-dom"
import { MeetingPrefillOptions, MeetingType } from "../../types/meetingTypes"
import { NewMeetingDropdown } from "../../components/scheduling/NewMeetingDropdown"
import { SvgPlus, SvgFilledInformationCircle } from "../../components/icons/"
import { useNavigate } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

interface Props {
  isTodayPage?: boolean
  isSchedulePage?: boolean
  setPickerOpen?: any
  pickerOpen?: any
}

const CoachScheduling: FC<Props> = ({
  isTodayPage = false,
  isSchedulePage = false,
  setPickerOpen,
  pickerOpen,
}) => {
  const { stopLoading } = useLoading()
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const [isCalendarConnected, setIsCalendarConnected] = useState<boolean>(false)
  const [showCalendar, setShowCalendar] = useState<boolean>(true)
  const [newMeetingType, setNewMeetingType] = useState<MeetingType>("custom")
  const [showNewMeeting, setShowNewMeeting] = useState<boolean>(false)
  const [showRescheduleMeeting, setShowRescheduleMeeting] =
    useState<boolean>(false)
  const [newMeetingPrefillOptions, setNewMeetingPrefillOptions] =
    useState<MeetingPrefillOptions | null>(null)
  const [hideSecondaryNav, setHideSecondaryNav] = useState<boolean>(false)
  const [userCalendars, setUserCalendars] = useState<any>({})
  const [userHasCalendar, setUserHasCalendar] = useState<boolean>(false)
  const [userCalendarsLoading, setUserCalendarsLoading] =
    useState<boolean>(false)
  const [clickReschedule, setClickReschedule] = useState<boolean>(false)
  const [clickTodayReschedule, setClickTodayReschedule] =
    useState<boolean>(false)
  const [backToContacts, setBackToContacts] = useState<boolean>(false)
  const [keepBackToButton, setKeepBackToButton] = useState<boolean>(false)
  const [contact, setContact] = useState<any>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const [showCalendarDetails, setShowCalendarDetails] = useState<boolean>(false)
  const [availabilities, setAvailabilities] = useState<any>([])
  const flagged = location.state?.flag

  const showComponent = (callback: any) => {
    setShowCalendar(false)
    setShowNewMeeting(false)
    callback(true)
  }

  useEffect(() => {
    if (showNewMeeting) {
      showComponent(setShowNewMeeting)
      setHideSecondaryNav(true)
    } else {
      setHideSecondaryNav(false)
    }
  }, [showNewMeeting])

  const createDefaultAvailability = () => {
    if (user) {
      const userDefaultAvailability = {
        ...defaultAvailability,
        timezone: user.timezone,
      }
      CalendarRequests.setAvailabilityRules({
        user_id: parseInt(user.user_id),
        availability_rules: [userDefaultAvailability],
      })
        .then(() => {})
        .catch((ex) => {
          toast.error(ex.response.data.message)
          stopLoading()
        })
    }
  }

  const getAvailabilityRules = () => {
    if (user) {
      CalendarRequests.getAvailabilityRules({
        user_id: parseInt(user.user_id),
      })
        .then((data) => {
          setAvailabilities(data.availability_rules)
        })
        .catch((ex) => {
          console.log(ex)
          toast.error(ex.response.data.message)
        })
    }
  }

  const completeRegisterCalendar = (code: string, data: any) => {
    const redirectUrl = `${process.env.REACT_APP_BASE_URL}/account-settings/calendars`
    if (code && user) {
      CalendarRequests.registerCalendar({
        user_id: parseInt(user.user_id),
        profile_count: data.profile_count,
        calendar_connect_code: code,
        redirect_url: redirectUrl,
      })
        .then(() => {
          toast.success("Calendar connected!")
          createDefaultAvailability()
          fetchUserCalendars()
        })
        // .then(() => {
        //   CalendarRequests.setAvailabilityRules({
        //     user_id: parseInt(user.user_id),
        //     availability_rules: availabilities
        //   })
        // })
        .catch((ex: any) => {
          toast.error(ex.response.data.message)
          stopLoading()
        })
      window.history.replaceState(null, "", window.location.pathname)
    }
  }

  const checkUrlParams = (data: any) => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get("code")
    const createMeeting = params.get("create")
    if (code && user) {
      completeRegisterCalendar(code, data)
      stopLoading()
    } else if (createMeeting) {
      setShowNewMeeting(true)
      setNewMeetingType("individual")
      stopLoading()
      window.history.replaceState(null, "", window.location.pathname)
    } else {
      window.history.replaceState(null, "", window.location.pathname)
      stopLoading()
    }
  }

  const fetchUserCalendars = () => {
    setUserCalendarsLoading(true)
    if (user) {
      CalendarRequests.getCalendarList({
        user_id: parseInt(user.user_id),
      })
        .then((data: any) => {
          setUserCalendarsLoading(false)
          setUserCalendars({
            profileCount: data.profile_count,
            calendars: data.calendars,
          })
          if (data.profile_count > 0) {
            setUserHasCalendar(true)
            setIsCalendarConnected(true)
          }
          checkUrlParams(data)
        })
        .catch((ex: any) => {
          setUserCalendarsLoading(false)
          console.log(ex)
          toast.error(ex.response.data.message)
          setUserCalendarsLoading(false)
        })
    } else {
      stopLoading()
    }
  }

  // Check location state and populate new meeting if service and contact data was sent
  useEffect(() => {
    let state = location.state
    if (state) {
      if (state.service && state.contact) {
        setNewMeetingPrefillOptions({
          service: state.service,
          contact: state.contact,
        })
        setNewMeetingType("individual")
        setShowNewMeeting(true)
      }
      if (!state.service && state.contact) {
        setNewMeetingPrefillOptions({ contact: state.contact })
        setNewMeetingType("individual")
        setShowNewMeeting(true)
      }
    }
  }, [location])

  useEffect(() => {
    fetchUserCalendars()
    getAvailabilityRules()
  }, [user])

  const handleCalendarClick = () => {
    navigate("/account-settings/calendars")
  }

  return (
    <>
      {isTodayPage && (
        <div className="hidden xl:block relative -mt-[68px] ml-[1073px]">
          <Button size="lg" asChild>
            <Link to="/coach/scheduling">
              New Meeting
              <SvgPlus />
            </Link>
          </Button>
        </div>
      )}
      {isTodayPage && (
        <>
          {userCalendarsLoading && <CalendarLoadingSpinner />}
          <div className={`mt-[12px] h-full flex w-full flex-col items-center`}>
            <div className="flex items-end w-full">
              {showCalendar && (
                <Calendar
                  setPickerOpen={setPickerOpen}
                  pickerOpen={pickerOpen}
                  isTodayPage={isTodayPage}
                  userHasCalendar={userHasCalendar}
                  setHideSecondaryNav={setHideSecondaryNav}
                  setNewMeetingType={setNewMeetingType}
                  setShowNewMeeting={setShowNewMeeting}
                  showRescheduleMeeting={showRescheduleMeeting}
                  setShowRescheduleMeeting={setShowRescheduleMeeting}
                  clickReschedule={clickReschedule}
                  setClickReschedule={setClickReschedule}
                  clickTodayReschedule={clickTodayReschedule}
                  backToContacts={backToContacts}
                  contact={contact}
                  setKeepBackToButton={setKeepBackToButton}
                  keepBackToButton={keepBackToButton}
                  userCalendars={userCalendars}
                  isCalendarConnected={isCalendarConnected}
                />
              )}
            </div>
          </div>
        </>
      )}

      {isSchedulePage && !isTodayPage && (
        <div className="container max-w-screen-lg">
          {flagged && (
            <div className="z-[200] mx-auto w-full flex flex-col bg-[#fcfcfc]">
              <div className="flex items-center mb-2">
                <p className="font-bold text-[36px] text-left">My Calendar</p>
                <Button
                  onClick={() => setShowCalendarDetails(true)}
                  variant="link"
                  size="icon"
                  className="ml-2"
                >
                  <SvgFilledInformationCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          {userCalendarsLoading && <CalendarLoadingSpinner />}
          <div className={`mt-[12px] w-full flex items-center flex-col`}>
            {!userCalendarsLoading && !isCalendarConnected && (
              <ConnectCalendarCTA isCalendarConnected={isCalendarConnected} />
            )}
            {isCalendarConnected && (
              <>
                <div className="lg:max-w-[1112px] w-full px-[16px] lg:flex items-center justify-between">
                  {!hideSecondaryNav && !showRescheduleMeeting && (
                    <>
                      <div className="flex items-center justify-center lg:justify-start mr-auto mb-[28px] lg:mb-0 lg:mx-auto overflow-x-auto lg:w-[1080px] text-graySlate text-base font-bold gap-[20px]">
                        <button
                          className={`${
                            showCalendar
                              ? "btn-primary btn-secondary bg-grayMist"
                              : "hover:bg-grayFlash duration-150 rounded-[10px]"
                          } px-[16px] py-[8px] lg:py-[12px] h-10`}
                          onClick={handleCalendarClick}
                        >
                          Calendar Settings
                        </button>
                      </div>
                      <div className="hidden lg:block relative lg:px-0 px-[20px]">
                        <NewMeetingDropdown
                          setNewMeetingType={setNewMeetingType}
                          setShowNewMeeting={setShowNewMeeting}
                          setKeepBackToButton={setKeepBackToButton}
                          setClickReschedule={setClickReschedule}
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="w-full">
                  {showCalendar && (
                    <Calendar
                      isSchedulePage={isSchedulePage}
                      userHasCalendar={userHasCalendar}
                      setHideSecondaryNav={setHideSecondaryNav}
                      setNewMeetingType={setNewMeetingType}
                      setShowNewMeeting={setShowNewMeeting}
                      showRescheduleMeeting={showRescheduleMeeting}
                      setShowRescheduleMeeting={setShowRescheduleMeeting}
                      clickReschedule={clickReschedule}
                      setClickReschedule={setClickReschedule}
                      clickTodayReschedule={clickTodayReschedule}
                      backToContacts={backToContacts}
                      contact={contact}
                      setKeepBackToButton={setKeepBackToButton}
                      keepBackToButton={keepBackToButton}
                      userCalendars={userCalendars}
                      isCalendarConnected={isCalendarConnected}
                    />
                  )}
                  {showNewMeeting && (
                    <NewMeeting
                      setShowCalendar={setShowCalendar}
                      setShowNewMeeting={setShowNewMeeting}
                      newMeetingType={newMeetingType}
                      setNewMeetingType={setNewMeetingType}
                      prefillOptions={newMeetingPrefillOptions}
                      showRescheduleMeeting={showRescheduleMeeting}
                      setShowRescheduleMeeting={setShowRescheduleMeeting}
                      clickReschedule={clickReschedule}
                      setClickReschedule={setClickReschedule}
                      clickTodayReschedule={clickTodayReschedule}
                      backToContacts={backToContacts}
                      contact={contact}
                      keepBackToButton={keepBackToButton}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showCalendarDetails && (
        <CalendarDetails setShowCalendarDetails={setShowCalendarDetails} />
      )}
    </>
  )
}

export default CoachScheduling
